import { gql, useMutation } from '@apollo/client';
import { Field, Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import {
  Message,
  ValidatedInput,
} from '../components/templates/template-components';
import { TeamQuery, TeamRemainingPaidSeatsQuery } from '../queries';
import { getFirstQueryResult } from '../utils/graphql';
import { useQueryWithMsg } from '../utils/hooks';
import TeamMembersView from './TeamMembersView';
import useMutateWithState from '../hooks/useMutateWithState';

const TeamBillingQuery = gql`
  query TeamBilling($id: ID!) {
    teamStripeState(id: $id) {
      id
      name
      updatedAt
      noSubscriptionNeeded
      stripeSubscription {
        id
        status
        validUntil
      }
      stripeCustomerId
    }
  }
`;

const UpdateTeamMutation = gql`
  mutation SuperadminUpdateTeam($input: SuperadminUpdateTeamInput!) {
    superadminUpdateTeam(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

const TeamForm = ({
  teamId,
  updatedAt,
  noSubscriptionNeeded,
  stripeCustomerId,
  stripeSubscription,
}) => {
  const initialValues = {
    noSubscriptionNeeded: noSubscriptionNeeded || false,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
  };

  const [updateTeam] = useMutation(UpdateTeamMutation, {
    refetchQueries: [
      { query: TeamQuery, variables: { id: teamId } },
      { query: TeamBillingQuery, variables: { id: teamId } },
      { query: TeamRemainingPaidSeatsQuery, variables: { id: teamId } },
    ],
    onError: (e) => console.error(e),
  });

  const [{ loading }, mutateAndSetState] = useMutateWithState();

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) =>
        mutateAndSetState(() =>
          updateTeam({
            variables: { input: { ...values, updatedAt, teamId } },
          }),
        )
      }
    >
      {({ isSubmitting, isValid, dirty, values }) => {
        return (
          <Form>
            {stripeSubscription == null && (
              <FormGroup check inline>
                <Label>No Subscription Needed?</Label>
                <Input
                  tag={Field}
                  name="noSubscriptionNeeded"
                  type="checkbox"
                  className="mx-2 mb-2"
                />
              </FormGroup>
            )}
            <ValidatedInput
              type="text"
              name="stripeCustomerId"
              label="Stripe Customer ID"
              placeholder={stripeCustomerId || 'Customer ID'}
              disabled={stripeCustomerId != null || values.noSubscriptionNeeded}
            />
            <ValidatedInput
              type="text"
              name="stripeSubscriptionId"
              label="Stripe Subscription ID"
              placeholder={stripeSubscription?.id || 'Subscription ID'}
              disabled={
                stripeSubscription != null || values.noSubscriptionNeeded
              }
            />
            {stripeSubscription && (
              <div className="pb-3">
                <div>
                  Subscription status: {stripeSubscription?.status || 'N/A'}
                </div>
                <div>
                  Valid until: {stripeSubscription?.validUntil || 'N/A'}
                </div>
              </div>
            )}
            <Button
              color="primary"
              type="submit"
              disabled={!dirty || isSubmitting || loading}
              outline={!isValid}
            >
              Update Team
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

const AdminTeamView = () => {
  const { teamId } = useParams();

  const { msg, data } = useQueryWithMsg(TeamBillingQuery, {
    variables: { id: teamId },
    skip: !teamId,
  });

  const teamData = getFirstQueryResult(data);

  if (msg) return <Message>{msg}</Message>;

  return (
    <>
      <div className="py-3">
        <TeamForm
          teamId={teamId}
          updatedAt={teamData?.updatedAt}
          noSubscriptionNeeded={teamData?.noSubscriptionNeeded}
          stripeCustomerId={teamData?.stripeCustomerId}
          stripeSubscription={teamData?.stripeSubscription}
        />
      </div>
      <TeamMembersView includeTeamInBreadcrumbs />
    </>
  );
};

export default AdminTeamView;
